/**
 * Hardware Maintenance Logs hook using React Relay
 *
 * Fetches maintenance logs for a specific hardware item.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useHardwareLogsRelayQuery as UseHardwareLogsRelayQuery,
  MaintenanceLogSortInput,
} from './__generated__/useHardwareLogsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for hardware maintenance logs with pagination
 */
const HardwareLogsQuery = graphql`
  query useHardwareLogsRelayQuery(
    $id: ID!
    $limit: Int
    $offset: Int
    $sort: [MaintenanceLogSortInput]
  ) {
    getHardware(id: $id) {
      id
      maintenanceLogs(limit: $limit, offset: $offset, sort: $sort) {
        id
        oldId
        createdOn
        notes
        timeTracking
        adminUserId
        hardwareOldId
        maintenanceTaskOldId
        maintenanceTicketOldId
        locationId
        systemId
        systemOldId
        organizationId
        adminUser {
          id
          email
          firstName
          lastName
        }
        maintenanceTask {
          id
          name
        }
        maintenanceTicket {
          id
          oldId
          description
        }
        location {
          id
          name
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { MaintenanceLogSortInput };

/**
 * Maintenance Log type from GraphQL
 */
export interface MaintenanceLogGraphQL {
  id: string;
  oldId?: string | null;
  createdOn: string;
  notes?: string | null;
  timeTracking: number;
  adminUserId: string;
  hardwareOldId?: string | null;
  maintenanceTaskOldId: string;
  maintenanceTicketOldId?: string | null;
  locationId?: string | null;
  systemId?: string | null;
  systemOldId: string;
  organizationId?: string | null;
  adminUser?: {
    id: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  maintenanceTask?: {
    id: string;
    name?: string | null;
  } | null;
  maintenanceTicket?: {
    id: string;
    oldId?: string | null;
    description?: string | null;
  } | null;
  location?: {
    id: string;
    name: string;
  } | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware maintenance logs using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareLogsTab({ hardwareId }: { hardwareId: string }) {
 *   return (
 *     <Suspense fallback={<Loading />}>
 *       <HardwareLogsContent hardwareId={hardwareId} />
 *     </Suspense>
 *   );
 * }
 *
 * function HardwareLogsContent({ hardwareId }: { hardwareId: string }) {
 *   const logs = useHardwareLogsRelay(hardwareId);
 *   return <LogsTable data={logs} />;
 * }
 */
export function useHardwareLogsRelay(
  hardwareId: string,
  options?: {
    limit?: number;
    offset?: number;
    sort?: MaintenanceLogSortInput[];
  }
) {
  const data = useLazyLoadQuery<UseHardwareLogsRelayQuery>(HardwareLogsQuery, {
    id: hardwareId,
    limit: options?.limit ?? 100,
    offset: options?.offset ?? 0,
    sort: options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }],
  });

  return (data.getHardware?.maintenanceLogs ?? []) as MaintenanceLogGraphQL[];
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware maintenance logs
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, isLoading, refetch } = useHardwareLogsRelayQuery(hardwareId);
 *
 * if (isLoading) return <Loading />;
 * return <LogsTable data={data} />;
 */
export function useHardwareLogsRelayQuery(
  hardwareId: string | undefined,
  options?: {
    enabled?: boolean;
    limit?: number;
    offset?: number;
    sort?: MaintenanceLogSortInput[];
  }
) {
  const [data, setData] = useState<MaintenanceLogGraphQL[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(hardwareId);
  const limit = options?.limit ?? 100;
  const offset = options?.offset ?? 0;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );

  useEffect(() => {
    if (!enabled || !hardwareId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

      const result = await fetchQuery<UseHardwareLogsRelayQuery>(
          relayEnvironment,
          HardwareLogsQuery,
          { id: hardwareId, limit, offset, sort }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData((result.getHardware?.maintenanceLogs ?? []) as MaintenanceLogGraphQL[]);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch logs'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, hardwareId, limit, offset, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, error, refetch]
  );
}
